import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaStar, FaArrowUp, FaArrowDown, FaArrowLeft } from 'react-icons/fa';
import { BiTrendingUp } from 'react-icons/bi';
import Loading from '../../components/Common/Loading';
import { fetchCoinsList, fetchSearchResults } from '../../utils/CoinGeckoCache';
import { useUser } from '../../context/UserContext';
import './styles.css';

const CryptoMarket = () => {
  const navigate = useNavigate();
  const { user, setShowLoginModal } = useUser();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [topCoins, setTopCoins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [favorites, setFavorites] = useState([]);

  // Load favorites from localStorage only if user is logged in
  useEffect(() => {
    if (user) {
      const savedFavorites = localStorage.getItem(`cryptoFavorites_${user.name}`);
      if (savedFavorites) {
        setFavorites(JSON.parse(savedFavorites));
      }
    } else {
      setFavorites([]);
    }
  }, [user]);

  // Save favorites to localStorage with user-specific key
  const saveFavorites = (newFavorites) => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    setFavorites(newFavorites);
    localStorage.setItem(`cryptoFavorites_${user.name}`, JSON.stringify(newFavorites));
  };

  // Toggle favorite - requires login
  const toggleFavorite = (coinId) => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    const newFavorites = favorites.includes(coinId)
      ? favorites.filter(id => id !== coinId)
      : [...favorites, coinId];
    saveFavorites(newFavorites);
  };

  // Fetch top cryptocurrencies with caching
  useEffect(() => {
    const fetchTopCoins = async () => {
      setLoading(true);
      try {
        const result = await fetchCoinsList({
          vs_currency: 'usd',
          order: 'market_cap_desc',
          per_page: 100,
          page: 1,
          sparkline: 'false',
          price_change_percentage: '24h'
        });
        
        setTopCoins(result.data);
        
        // Log cache information for debugging
        if (result.fromCache) {
          console.log(`Loaded market data from cache (age: ${Math.round(result.cacheAge / 1000)}s)`);
        } else {
          console.log('Loaded market data from API');
        }
      } catch (err) {
        setError('Failed to load market data');
        console.error('Error fetching top coins:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTopCoins();
  }, []);

  // Search coins - first try to filter existing data, then search API
  const searchCoins = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    
    // First, try to find matches in the loaded top coins
    const localMatches = topCoins.filter(coin => 
      coin.name.toLowerCase().includes(query.toLowerCase()) ||
      coin.symbol.toLowerCase().includes(query.toLowerCase())
    );
    
    if (localMatches.length > 0) {
      setSearchResults(localMatches);
      setSearchLoading(false);
      return;
    }

    // If no local matches, search via API with caching
    try {
      const result = await fetchSearchResults(query);
      setSearchResults(result.data.coins.slice(0, 20));
      setError(null);
      
      // Log cache information for debugging
      if (result.fromCache) {
        console.log(`Loaded search results from cache (age: ${Math.round(result.cacheAge / 1000)}s)`);
      } else {
        console.log('Loaded search results from API');
      }
    } catch (err) {
      setError('Search failed. Please try again.');
      console.error('Search error:', err);
    } finally {
      setSearchLoading(false);
    }
  }, [topCoins]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchCoins(searchTerm);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchCoins]);

  // Navigate to coin details
  const handleCoinClick = (coinId) => {
    navigate(`/coins/${coinId}`);
  };

  // Format functions
  const formatPrice = (price) => {
    if (!price) return 'N/A';
    if (price < 0.000001) return `$${price.toFixed(8)}`;
    if (price < 0.001) return `$${price.toFixed(6)}`;
    if (price < 0.01) return `$${price.toFixed(4)}`;
    if (price < 1) return `$${price.toFixed(3)}`;
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatNumber = (num) => {
    if (!num) return 'N/A';
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
    return `$${num.toFixed(2)}`;
  };

  const formatSupply = (num) => {
    if (!num) return 'N/A';
    if (num >= 1e12) return `${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
    return num.toLocaleString();
  };

  const formatPercentage = (change) => {
    if (!change && change !== 0) return 'N/A';
    const formatted = change.toFixed(2);
    return change >= 0 ? `+${formatted}%` : `${formatted}%`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'N/A';
    const now = new Date();
    const date = new Date(dateString);
    const diffInMinutes = Math.floor((now - date) / 1000 / 60);
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  return (
    <div className="crypto-market-container">
      {/* Mobile Back Button */}
      <div className="mobile-back-button">
        <button className="back-button" onClick={() => navigate('/')}>
          <FaArrowLeft /> Back to Home
        </button>
      </div>

      <div className="crypto-market-header">
        <h1>
          <BiTrendingUp className="header-icon" />
          Cryptocurrency Market
        </h1>
        <p>Explore and track thousands of cryptocurrencies with real-time market data</p>
      </div>

      {/* Search Section */}
      <div className="search-section">
        <div className="search-bar">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search cryptocurrencies (e.g., Bitcoin, Ethereum, BTC, ETH...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="crypto-search-input"
          />
          {searchLoading && <div className="search-spinner"></div>}
        </div>

        {/* Search Results */}
        {searchTerm && searchResults.length > 0 && (
          <div className="search-results-section">
            <h3>Search Results</h3>
            <div className="coins-grid">
              {searchResults.map((coin) => (
                <div
                  key={coin.id}
                  className="coin-card search-result"
                  onClick={() => handleCoinClick(coin.id)}
                >
                  <div className="coin-card-header">
                    <img 
                      src={coin.image || coin.large} 
                      alt={coin.name} 
                      className="coin-logo" 
                    />
                    <div className="coin-info">
                      <h4 className="coin-name">{coin.name}</h4>
                      <span className="coin-symbol">{coin.symbol.toUpperCase()}</span>
                    </div>
                    <button
                      className={`favorite-btn ${favorites.includes(coin.id) ? 'favorited' : ''} ${!user ? 'login-required' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(coin.id);
                      }}
                      title={!user ? 'Login to add favorites' : favorites.includes(coin.id) ? 'Remove from favorites' : 'Add to favorites'}
                      disabled={!user}
                    >
                      <FaStar />
                    </button>
                  </div>
                  <div className="coin-rank">
                    #{coin.market_cap_rank || 'N/A'}
                  </div>
                  {coin.current_price && (
                    <div className="coin-price">
                      <span className="price-value">{formatPrice(coin.current_price)}</span>
                      {coin.price_change_percentage_24h !== undefined && (
                        <span className={`change-value ${coin.price_change_percentage_24h >= 0 ? 'positive' : 'negative'}`}>
                          {coin.price_change_percentage_24h >= 0 ? <FaArrowUp /> : <FaArrowDown />}
                          {formatPercentage(coin.price_change_percentage_24h)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No Search Results */}
        {searchTerm && !searchLoading && searchResults.length === 0 && !error && (
          <div className="no-results">
            <p>No cryptocurrencies found for "{searchTerm}". Try a different search term.</p>
          </div>
        )}
      </div>

      {/* Top Cryptocurrencies */}
      {!searchTerm && (
        <div className="top-coins-section">
          <h2>Top Cryptocurrencies by Market Cap</h2>
          <div className="table-scroll-hint">
            <p>💡 Scroll horizontally to see all data columns</p>
          </div>
          {loading ? (
            <Loading />
          ) : error ? (
            <div className="error-message">
              <p>{error}</p>
              <button onClick={() => window.location.reload()} className="retry-btn">
                Try Again
              </button>
            </div>
          ) : (
            <div className="coins-table">
              <div className="table-header">
                <div className="rank-col">#</div>
                <div className="name-col">Name</div>
                <div className="price-col">Price</div>
                <div className="change-col">24h Change</div>
                <div className="high-low-col">24h High/Low</div>
                <div className="volume-col">Volume (24h)</div>
                <div className="market-cap-col">Market Cap</div>
                <div className="supply-col">Circulating Supply</div>
                <div className="ath-col">ATH</div>
                <div className="atl-col">ATL</div>
                <div className="updated-col">Last Updated</div>
                <div className="favorite-col">Watch</div>
              </div>
              
              {topCoins.map((coin, index) => (
                <div
                  key={coin.id}
                  className="table-row"
                  onClick={() => handleCoinClick(coin.id)}
                >
                  {/* Mobile Card Layout */}
                  <div className="mobile-card-content">
                    <div className="rank-col">
                      <span className="rank-number">#{coin.market_cap_rank || 'N/A'}</span>
                    </div>
                    
                    <div className="favorite-col">
                      <button
                        className={`favorite-btn-table ${favorites.includes(coin.id) ? 'favorited' : ''} ${!user ? 'login-required' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(coin.id);
                        }}
                        title={!user ? 'Login to add favorites' : favorites.includes(coin.id) ? 'Remove from favorites' : 'Add to favorites'}
                        disabled={!user}
                      >
                        <FaStar />
                      </button>
                    </div>

                    <div className="name-col">
                      <div className="coin-name-info">
                        <img src={coin.image} alt={coin.name} className="coin-logo-small" />
                        <div>
                          <div className="coin-name-table">{coin.name}</div>
                          <div className="coin-symbol-table">{coin.symbol.toUpperCase()}</div>
                        </div>
                      </div>
                    </div>

                    <div className="mobile-price-section">
                      <div className="price-col">
                        <span className="price-value">{formatPrice(coin.current_price)}</span>
                      </div>
                      
                      <div className="change-col">
                        <span className={`change-value ${coin.price_change_percentage_24h >= 0 ? 'positive' : 'negative'}`}>
                          {coin.price_change_percentage_24h >= 0 ? <FaArrowUp /> : <FaArrowDown />}
                          {formatPercentage(coin.price_change_percentage_24h)}
                        </span>
                        <div className="price-change-amount">
                          {coin.price_change_24h ? formatPrice(Math.abs(coin.price_change_24h)) : 'N/A'}
                        </div>
                      </div>
                    </div>

                    <div className="mobile-high-low">
                      <div className="high-low-col">
                        <div className="high-value">H: {formatPrice(coin.high_24h)}</div>
                      </div>
                      <div className="high-low-col">
                        <div className="low-value">L: {formatPrice(coin.low_24h)}</div>
                      </div>
                    </div>

                    <div className="mobile-stats-grid">
                      <div className="mobile-stat-item">
                        <div className="mobile-stat-label">Volume</div>
                        <div className="mobile-stat-value">{formatNumber(coin.total_volume)}</div>
                      </div>
                      <div className="mobile-stat-item">
                        <div className="mobile-stat-label">Market Cap</div>
                        <div className="mobile-stat-value">{formatNumber(coin.market_cap)}</div>
                      </div>
                    </div>

                    <div className="mobile-expandable">
                      <div className="mobile-expand-content">
                        <div className="mobile-expand-item">
                          <div className="mobile-expand-label">Circulating</div>
                          <div className="mobile-expand-value">
                            {formatSupply(coin.circulating_supply)}
                          </div>
                        </div>
                        <div className="mobile-expand-item">
                          <div className="mobile-expand-label">ATH</div>
                          <div className="mobile-expand-value">
                            {formatPrice(coin.ath)}
                          </div>
                        </div>
                        <div className="mobile-expand-item">
                          <div className="mobile-expand-label">ATL</div>
                          <div className="mobile-expand-value">
                            {formatPrice(coin.atl)}
                          </div>
                        </div>
                        <div className="mobile-expand-item">
                          <div className="mobile-expand-label">Updated</div>
                          <div className="mobile-expand-value">
                            {formatTimeAgo(coin.last_updated)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Desktop Table Layout - Hidden on Mobile */}
                  <div className="desktop-table-content">
                    <div className="rank-col">
                      <span className="rank-number">{coin.market_cap_rank || 'N/A'}</span>
                    </div>
                    
                    <div className="name-col">
                      <div className="coin-name-info">
                        <img src={coin.image} alt={coin.name} className="coin-logo-small" />
                        <div>
                          <div className="coin-name-table">{coin.name}</div>
                          <div className="coin-symbol-table">{coin.symbol.toUpperCase()}</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="price-col">
                      <span className="price-value">{formatPrice(coin.current_price)}</span>
                    </div>
                    
                    <div className="change-col">
                      <span className={`change-value ${coin.price_change_percentage_24h >= 0 ? 'positive' : 'negative'}`}>
                        {coin.price_change_percentage_24h >= 0 ? <FaArrowUp /> : <FaArrowDown />}
                        {formatPercentage(coin.price_change_percentage_24h)}
                      </span>
                      <div className="price-change-amount">
                        {coin.price_change_24h ? formatPrice(Math.abs(coin.price_change_24h)) : 'N/A'}
                      </div>
                    </div>

                    <div className="high-low-col">
                      <div className="high-value">H: {formatPrice(coin.high_24h)}</div>
                      <div className="low-value">L: {formatPrice(coin.low_24h)}</div>
                    </div>
                    
                    <div className="volume-col">
                      <span className="volume-value">{formatNumber(coin.total_volume)}</span>
                    </div>
                    
                    <div className="market-cap-col">
                      <span className="market-cap-value">{formatNumber(coin.market_cap)}</span>
                      {coin.market_cap_change_percentage_24h && (
                        <div className={`market-cap-change ${coin.market_cap_change_percentage_24h >= 0 ? 'positive' : 'negative'}`}>
                          {formatPercentage(coin.market_cap_change_percentage_24h)}
                        </div>
                      )}
                    </div>

                    <div className="supply-col">
                      <div className="circulating-supply">
                        {formatSupply(coin.circulating_supply)} {coin.symbol.toUpperCase()}
                      </div>
                      {coin.total_supply && (
                        <div className="total-supply">
                          Total: {formatSupply(coin.total_supply)}
                        </div>
                      )}
                      {coin.max_supply && (
                        <div className="max-supply">
                          Max: {formatSupply(coin.max_supply)}
                        </div>
                      )}
                    </div>

                    <div className="ath-col">
                      <div className="ath-price">{formatPrice(coin.ath)}</div>
                      <div className="ath-date">{formatDate(coin.ath_date)}</div>
                      {coin.ath_change_percentage && (
                        <div className="ath-change negative">
                          {formatPercentage(coin.ath_change_percentage)}
                        </div>
                      )}
                    </div>

                    <div className="atl-col">
                      <div className="atl-price">{formatPrice(coin.atl)}</div>
                      <div className="atl-date">{formatDate(coin.atl_date)}</div>
                      {coin.atl_change_percentage && (
                        <div className="atl-change positive">
                          {formatPercentage(coin.atl_change_percentage)}
                        </div>
                      )}
                    </div>

                    <div className="updated-col">
                      <div className="last-updated">{formatTimeAgo(coin.last_updated)}</div>
                      {coin.fully_diluted_valuation && (
                        <div className="fdv">
                          FDV: {formatNumber(coin.fully_diluted_valuation)}
                        </div>
                      )}
                    </div>
                    
                    <div className="favorite-col">
                      <button
                        className={`favorite-btn-table ${favorites.includes(coin.id) ? 'favorited' : ''} ${!user ? 'login-required' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(coin.id);
                        }}
                        title={!user ? 'Login to add favorites' : favorites.includes(coin.id) ? 'Remove from favorites' : 'Add to favorites'}
                        disabled={!user}
                      >
                        <FaStar />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CryptoMarket;